import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Product } from '../types';
import { productsApi } from '../lib/api';
import { mapProduct } from '../lib/mappers';
import { placeholders } from '../lib/placeholders';
import { navigate } from '../lib/router';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const searchProducts = async () => {
      if (searchTerm.trim().length < 2) {
        setResults([]);
        return;
      }

      setIsLoading(true);
      const { data, error } = await productsApi.getAll({ search: searchTerm, limit: 10 });
      if (!error) {
        setResults((data?.products || []).map(mapProduct));
      } else {
        console.error('Search failed', error);
      }
      setIsLoading(false);
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={onClose}
      />
      <div className="fixed top-0 left-0 right-0 z-50 animate-slide-down border-b border-primary/10 bg-background shadow-[0_18px_48px_rgba(13,13,13,0.12)]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/55" size={20} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for products..."
                className="w-full rounded-lg border border-primary/15 bg-white/70 py-3 pl-12 pr-4 text-text placeholder:text-charcoal/55 focus:border-primary focus:outline-none"
                autoFocus
              />
            </div>
            <button
              onClick={onClose}
              className="text-charcoal/70 transition-colors hover:text-primary"
            >
              <X size={24} />
            </button>
          </div>

          {searchTerm.trim().length >= 2 && (
            <div className="mt-6 max-h-96 overflow-y-auto">
              {isLoading ? (
                <p className="text-center text-charcoal/70">Searching...</p>
              ) : results.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.map((product) => (
                    <a
                      key={product.id}
                      href={`/product/${product.id}`}
                      className="flex items-center gap-3 rounded-2xl border border-primary/10 bg-white/70 p-3 transition-colors hover:bg-secondary/70"
                      onClick={(e) => {
                        e.preventDefault();
                        onClose();
                        setSearchTerm('');
                        navigate(`/product/${product.id}`);
                      }}
                    >
                      <img
                        src={product.image_url || product.images?.[0] || placeholders.small}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h3 className="line-clamp-1 text-sm font-semibold text-text">
                          {product.name}
                        </h3>
                        <p className="text-sm font-semibold text-primary">₹{product.price}</p>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-center text-charcoal/70">No products found</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
